import { useEvent, useSyncRef } from '@react-aria/utils';
import * as React from 'react';
import {
    type AriaOverlayProps,
    FocusScope,
    type FocusScopeProps,
    useObjectRef,
    useOverlay,
} from 'react-aria';

import { assert } from '@gitbook/util-ts';

import { AnimatedStateContext } from '../animations';
import { checkIsElementContainsInOverlay } from '../util';
import { useContextMenuOutside } from '../util/useContextMenuOutside';
import { OverlayContext } from './OverlayContext';
import { getOverlayProps, OverlayTriggerContext } from './OverlayTrigger';

type OverlayElementRef = React.MutableRefObject<HTMLDivElement | null>;

const OverlayStackContext = React.createContext<React.MutableRefObject<OverlayElementRef[]>>({
    current: [],
});

export const OverlayFocusContainedContext = React.createContext<boolean>(true);

export type OverlayProps = React.ComponentPropsWithoutRef<'div'> &
    Pick<FocusScopeProps, 'autoFocus' | 'contain' | 'restoreFocus'> &
    Pick<AriaOverlayProps, 'isDismissable' | 'shouldCloseOnInteractOutside'>;

/**
 * A container which renders an overlay such as a popover or modal in a portal,
 * and provides a focus scope for the child elements.
 */
export function Overlay({
    ref: forwardedRef,
    ...props
}: OverlayProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    const trigger = React.useContext(OverlayTriggerContext);
    const stackRef = React.useContext(OverlayStackContext);
    assert(
        context !== null && animatedState !== null && trigger !== null,
        'Overlay must be used within a OverlayRoot'
    );

    const {
        autoFocus = true,
        contain = true,
        restoreFocus = true,
        isDismissable: isDismissableProp = true,
        shouldCloseOnInteractOutside = () => true,
        ...domProps
    } = props;

    const isExiting = animatedState === 'exiting';
    const isExited = animatedState === 'exited';

    const ref = useObjectRef<HTMLDivElement>(forwardedRef);
    useSyncRef(context, ref);

    // When modal auto-focus is disabled, focus the container so the focus scope still traps tab navigation.
    React.useLayoutEffect(() => {
        const element = ref.current;
        const isModalDialog = domProps.role === 'dialog' || domProps.role === 'alertdialog';

        if (
            autoFocus ||
            !contain ||
            !isModalDialog ||
            !element ||
            element.contains(document.activeElement)
        ) {
            return;
        }

        element.focus({ preventScroll: true });
    }, [autoFocus, contain, domProps.role, ref]);

    React.useEffect(() => {
        stackRef.current.push(ref);
        return () => {
            const index = stackRef.current.indexOf(ref);
            if (index !== -1) {
                stackRef.current.splice(index, 1);
            }
        };
    }, [stackRef, ref]);

    const hasInteractedOutside = useHasInteractedOutside(ref);

    const isFocusContained =
        React.useContext(OverlayFocusContainedContext) &&
        !isExiting &&
        !isExited &&
        !hasInteractedOutside &&
        contain;

    const isClosable = context.isClosable && !context.isPending;
    const isDismissable = isClosable && isDismissableProp;
    const isOpen = !isExiting && !isExited;

    const canCloseOnInteractOutside = (element: Element) => {
        const stack = stackRef.current;
        if (
            !isClosable ||
            !shouldCloseOnInteractOutside(element) ||
            !shouldCloseOnInteractOutsideOverlay(element)
        ) {
            return false;
        }
        const stackIndex = stack.indexOf(ref);
        if (stackIndex === -1) {
            return true;
        }
        const nextStackSiblings = stack.slice(stackIndex);
        return !nextStackSiblings.some((ref) => ref.current?.contains(element));
    };

    const { overlayProps } = useOverlay(
        {
            isDismissable,
            onClose: trigger.close,
            isOpen,
            shouldCloseOnBlur: false,
            shouldCloseOnInteractOutside: canCloseOnInteractOutside,
            isKeyboardDismissDisabled: !isClosable,
        },
        ref
    );

    useContextMenuOutside({
        ref,
        onInteractOutside: (event) => {
            if (event.target instanceof Element && canCloseOnInteractOutside(event.target)) {
                trigger.close();
            }
        },
        isDisabled: !isOpen || !isDismissable,
    });

    // Get the props to be applied for the trigger to work properly.
    const triggerProps = getOverlayProps(trigger, domProps);

    const content = (
        <OverlayStackContext.Provider value={stackRef}>
            <div
                ref={ref}
                data-spine-overlay=""
                tabIndex={-1}
                {...overlayProps}
                {...domProps}
                {...triggerProps}
            />
        </OverlayStackContext.Provider>
    );

    // For hover, there is no focus scope.
    if (trigger.action === 'hover') {
        return content;
    }

    return (
        <FocusScope contain={isFocusContained} restoreFocus={restoreFocus} autoFocus={autoFocus}>
            {content}
        </FocusScope>
    );
}

/**
 * Check if the overlay should close when an external element is interacted.
 */
export function shouldCloseOnInteractOutsideOverlay(element: Element) {
    return !checkIsElementContainsInOverlay(element);
}

/**
 * Check if the user has interacted outside the element provided as ref.
 */
function useHasInteractedOutside(ref: React.RefObject<HTMLElement | null>) {
    const [pointed, setPointed] = React.useState(false);
    const documentRef = React.useRef(document);
    useEvent(
        documentRef,
        'pointerdown',
        (event) => {
            const { target } = event;
            const refElement = ref.current;
            setPointed(
                Boolean(
                    target instanceof Element &&
                    refElement &&
                    !refElement.contains(target) &&
                    checkIsElementContainsInOverlay(target)
                )
            );
        },
        { capture: true }
    );
    return pointed;
}
